import { Box } from '@mui/material';
import { type ReactElement, type ReactNode, useCallback, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { ModalStepContext, ModalStepsContext } from './modal-steps-context';

let stepSeed = 0;

interface StackedStep {
  id: number;
  element: ReactElement;
  resolve: (value: unknown) => void;
  returnFocus: HTMLElement | null;
}

interface ModalStepSlotProps {
  index: number;
  isTop: boolean;
  stepId?: number;
  onPop?: (id: number, value?: unknown) => void;
  children: ReactNode;
}

const ModalStepSlot = ({ index, isTop, stepId, onPop, children }: ModalStepSlotProps) => {
  const value = useMemo(
    () => ({
      index,
      isTop,
      popStep:
        stepId === undefined || !onPop
          ? undefined
          : (popped?: unknown) => onPop(stepId, popped),
    }),
    [index, isTop, stepId, onPop],
  );

  return (
    <ModalStepContext.Provider value={value}>
      <Box sx={{ display: isTop ? 'contents' : 'none' }}>{children}</Box>
    </ModalStepContext.Provider>
  );
};

interface ModalStepStackProps {
  root: ReactNode;
}

export const ModalStepStack = ({ root }: ModalStepStackProps) => {
  const [steps, setSteps] = useState<StackedStep[]>([]);

  const pushStep = useCallback(
    <T,>(element: ReactElement) =>
      new Promise<T | undefined>((resolve) => {
        stepSeed += 1;
        const id = stepSeed;
        // The value a step pops with is whatever its opener asked for; like any promise-returning
        // dialog, that link is a convention the types cannot check, so the resolver is widened here.
        const resolveStep = resolve as (value: unknown) => void;
        const returnFocus =
          document.activeElement instanceof HTMLElement ? document.activeElement : null;
        setSteps((current) => [
          ...current,
          { id, element, resolve: resolveStep, returnFocus },
        ]);
      }),
    [],
  );

  const popStep = useCallback(
    (id: number, value?: unknown) => {
      const at = steps.findIndex((step) => step.id === id);
      if (at === -1) {
        return;
      }
      steps
        .slice(at)
        .forEach((step, offset) => step.resolve(offset === 0 ? value : undefined));
      // The step below is display:none until this commits, and a hidden element cannot take focus.
      flushSync(() => setSteps((current) => current.slice(0, at)));
      const { returnFocus } = steps[at];
      if (returnFocus?.isConnected) {
        returnFocus.focus();
      }
    },
    [steps],
  );

  const depth = steps.length;
  const stepsValue = useMemo(() => ({ pushStep, depth }), [pushStep, depth]);

  return (
    <ModalStepsContext.Provider value={stepsValue}>
      <ModalStepSlot index={0} isTop={depth === 0}>
        {root}
      </ModalStepSlot>
      {steps.map((step, offset) => (
        <ModalStepSlot
          key={step.id}
          index={offset + 1}
          isTop={offset === depth - 1}
          stepId={step.id}
          onPop={popStep}
        >
          {step.element}
        </ModalStepSlot>
      ))}
    </ModalStepsContext.Provider>
  );
};
